import {
  ArcSegment,
  CompoundCurve,
  GetArcVertices,
} from '@technology/web3d-plugin-line-editing';

import {
  FeatureGeometry,
  LineStringGeometry,
  PolygonGeometry,
  Position3D,
} from './geometry';

const MIN_POLYGON_VERTICES = 3;
const ARC_TESSELLATION_VERTICES = 25;
const CLOSED_RING_EPSILON = 1e-6;
const Z_INDEX = 2;
export const AREA_TEMPLATE_GEOMETRY_TYPE = 'Area';

export interface SceneVertex {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export function closedRing(vertices: readonly Position3D[]): Position3D[] {
  if (vertices.length === 0) {
    return [];
  }
  if (areSamePosition(vertices[0], vertices[vertices.length - 1])) {
    return [...vertices];
  }
  return [...vertices, vertices[0]];
}

export function compoundCurveSceneVertices(
  curve: CompoundCurve
): readonly SceneVertex[] {
  const segments = curve.getSegments();
  const hasArcs = segments.some((segment) => segment instanceof ArcSegment);
  if (!hasArcs) {
    return curve.getPoints();
  }

  const vertices: SceneVertex[] = [];
  for (const segment of segments) {
    if (segment instanceof ArcSegment) {
      const sampled = uniqueArcVertices(
        GetArcVertices(
          segment.start,
          segment.middle,
          segment.end,
          ARC_TESSELLATION_VERTICES
        )
      );
      for (let i = 0; i < sampled.length - 1; i++) {
        vertices.push(sampled[i]);
      }
    } else {
      vertices.push(segment.start);
    }
  }
  const lastSegment = segments[segments.length - 1];
  // eslint-disable-next-line sonarjs/different-types-comparison -- index access can be undefined at runtime (noUncheckedIndexedAccess is off)
  if (!curve.isClosed() && lastSegment !== undefined) {
    vertices.push(lastSegment.end);
  }
  return vertices;
}

export function featureGeometryFromCurveCoordinates(
  curve: CompoundCurve,
  coordinates: readonly Position3D[]
): LineStringGeometry | PolygonGeometry {
  if (shouldPersistAsPolygon(curve, coordinates)) {
    const geometry: PolygonGeometry = {
      type: 'Polygon',
      coordinates: [closedRing(coordinates)],
    };
    return geometry;
  }
  const geometry: LineStringGeometry = {
    type: 'LineString',
    coordinates: [...coordinates],
  };
  return geometry;
}

export function featureGeometryFromCurve(
  curve: CompoundCurve
): FeatureGeometry {
  const coordinates = compoundCurveSceneVertices(curve).map(
    (vertex): Position3D => [vertex.x, vertex.y, vertex.z]
  );
  return featureGeometryFromCurveCoordinates(curve, coordinates);
}

export function geometryForLayerTemplate(
  geometry: FeatureGeometry,
  templateType: string | undefined,
  sourceGeometryType?: string
): FeatureGeometry {
  if (geometry.type !== 'Polygon') {
    return geometry;
  }
  const shouldPersistAsArea =
    templateType === AREA_TEMPLATE_GEOMETRY_TYPE ||
    (templateType === undefined && sourceGeometryType !== 'LineString');
  if (shouldPersistAsArea) {
    return geometry;
  }
  return { type: 'LineString', coordinates: [...geometry.coordinates[0]] };
}

function shouldPersistAsPolygon(
  curve: CompoundCurve,
  coordinates: readonly Position3D[]
): boolean {
  const uniqueCount = uniqueVertexCount(coordinates);
  if (uniqueCount < MIN_POLYGON_VERTICES) {
    return false;
  }
  return curve.isClosed() || isGeometricallyClosed(coordinates);
}

function isGeometricallyClosed(coordinates: readonly Position3D[]): boolean {
  if (coordinates.length < MIN_POLYGON_VERTICES) {
    return false;
  }
  return areSamePosition(coordinates[0], coordinates[coordinates.length - 1]);
}

function uniqueVertexCount(coordinates: readonly Position3D[]): number {
  if (coordinates.length === 0) {
    return 0;
  }
  return isGeometricallyClosed(coordinates)
    ? coordinates.length - 1
    : coordinates.length;
}

function uniqueArcVertices(
  vertices: readonly { x: number; y: number; z: number }[]
): SceneVertex[] {
  const unique: SceneVertex[] = [];
  for (const vertex of vertices) {
    const previous = unique[unique.length - 1];
    if (
      // eslint-disable-next-line sonarjs/different-types-comparison -- index access can be undefined at runtime (noUncheckedIndexedAccess is off)
      previous !== undefined &&
      previous.x === vertex.x &&
      previous.y === vertex.y &&
      previous.z === vertex.z
    ) {
      continue;
    }
    unique.push(vertex);
  }
  return unique;
}

function areSamePosition(a: Position3D, b: Position3D): boolean {
  return (
    Math.abs(a[0] - b[0]) <= CLOSED_RING_EPSILON &&
    Math.abs(a[1] - b[1]) <= CLOSED_RING_EPSILON &&
    Math.abs((a[Z_INDEX] ?? 0) - (b[Z_INDEX] ?? 0)) <= CLOSED_RING_EPSILON
  );
}
